import { BUSINESS_TYPE, FormData } from '@/api/types';
import {
  generateIban,
  generateNip,
  generatePesel,
  generateRegon,
} from '@/components/devtools/generateData';

import { fakerPL as faker } from '@faker-js/faker';

export const fullOC = {
  accident_in_poland: 'yes',
  polish_plates: 'yes',
  accident_reported_to_insurer: 'yes_my_insure',
  agreement_or_settlement: 'yes',
  agreement_or_settlement_concluded: [
    { label: 'Koszty holowania', value: 'towing_costs' },
  ],
  vehicle_occupant_injury: 'does_not_know',
  accident_province_id: 'PROVINCE_KUJAWSKO_POMORSKIE',
  risky_area: 'no',
  car_condition: 'sold_repaired',
  insurance_company: '10',
  insurer_pay_compensation: 'yes',
  type_of_damage: 'full_oc',
  vehicle_sold_price: '111.00',
  vehicle_value_before_crash: 'upper_10k',
  wheel_side: 'right',
  has_damage_photos: 'yes',
  repair_documents_provided_to_ic: [
    {
      label: '\u017badne z powy\u017cszych',
      value: 'any',
      willResetRest: true,
    },
  ],
  damages_before_accident: [
    {
      group: 'Prawy bok',
      label: 'Drzwi prawe przednie',
      value: 'right_front_door',
    },
  ],
  repairs_before_accident: 'no',
  unrepairable_damages_before_accident: 'yes',
};

export const partOC = {
  accident_reported_to_insurer: 'yes_my_insure',
  insurance_company: '10',
  type_of_damage: 'part_oc',
  accident_in_poland: 'yes',
  polish_plates: 'yes',
  vehicle_occupant_injury: 'does_not_know',
  accident_province_id: 'PROVINCE_KUJAWSKO_POMORSKIE',
  risky_area: 'no',
  car_condition: 'repaired',
  wheel_side: 'left',
  wheel_side_factory_installed: 'yes',
  insurer_pay_compensation: 'yes',
  agreement_or_settlement: 'no',
  has_damage_photos: 'yes',
  repair_documents_provided_to_ic: [
    {
      label: '\u017badne z powy\u017cszych',
      value: 'any',
      willResetRest: true,
    },
  ],
  damages_before_accident: [
    {
      group: 'Prawy bok',
      label: 'Drzwi prawe przednie',
      value: 'right_front_door',
    },
  ],
  still_vehicle_owner: 'yes',
  unrepairable_damages_before_accident: 'yes',
  repairs_before_accident: 'no',
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { polish_plates: fullOC_polish_plates, ...restFullOC } = fullOC;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { polish_plates: partOC_polish_plates, ...restPartOC } = partOC;

export const fullAC = {
  ...restFullOC,
  type_of_damage: 'full_ac',
};

export const partAC = {
  ...restPartOC,
  type_of_damage: 'part_ac',
};

export const surveyThree = {
  business_activity: 'no',
  damages_before_accident: [
    {
      group: 'Prawy bok',
      label: 'Drzwi prawe przednie',
      value: 'right_front_door',
    },
  ],
  repairs_before_accident: 'no',
  still_vehicle_owner: 'yes',
  unrepairable_damages_before_accident: 'yes',
};

export const surveyThreeJDG = {
  ...surveyThree,
  business_activity: 'yes',
  vehicle_used_for_business: 'yes',
  business_type: 'jdg',
  vat_payer_status: 'yes',
  vehicle_as_fixed_asset: 'yes',
  vat_deduction_type: '100',
};

export const surveyThreeZOO = {
  ...surveyThreeJDG,
  business_type: 'zoo',
};

export const surveyOtherCompany = {
  ...surveyThreeJDG,
  business_type: 'sc',
};

export const surveyFour = {
  ...surveyThree,
  damage_rights_assignments: [
    {
      label: '\u017badne z powy\u017cszych',
      value: 'any',
      willResetRest: true,
    },
  ],
};

export const surveyFourCompanies = {
  ...surveyThreeZOO,
  damage_rights_assignments: [
    {
      label: '\u017badne z powy\u017cszych',
      value: 'any',
      willResetRest: true,
    },
  ],
  repair_documents_provided_to_ic: [
    {
      label: '\u017badne z powy\u017cszych',
      value: 'any',
      willResetRest: true,
    },
  ],
};

export const surveyFourCompaniesOther = {
  ...surveyFourCompanies,
  business_type: 'sc',
};

export const surveyFourJDG = {
  ...surveyThreeJDG,
  damage_rights_assignments: [
    {
      label: '\u017badne z powy\u017cszych',
      value: 'any',
      willResetRest: true,
    },
  ],
  repair_documents_provided_to_ic: [
    {
      label: '\u017badne z powy\u017cszych',
      value: 'any',
      willResetRest: true,
    },
  ],
};

const ownerBase = (data: FormData, options?: { controlled?: boolean }) => ({
  account_is_owner: true,
  account_number: generateIban(),
  account_owner_first_name: data.user.name,
  account_owner_last_name: faker.person.lastName(),
  address: options?.controlled
    ? {
        label: 'Kazimierza Pułaskiego',
        value: 198537,
      }
    : faker.location.street(),
  building_number: faker.location.buildingNumber(),
  city: options?.controlled
    ? {
        label: 'Ostrowski > Ostrów Wielkopolski > Ostrów Wielkopolski',
        value: 32719,
      }
    : faker.location.city(),
  email: data.user.email,
  first_name: data.user.name,
  flat_number: '2',
  last_name: faker.person.lastName(),
  pesel: generatePesel(
    faker.date.anytime(),
    faker.helpers.arrayElement(['male', 'female'])
  ),
  phone:
    data.user.phone ??
    faker.helpers.rangeToNumber({ min: 500000000, max: 999999999 }).toString(),
  zip_code: options?.controlled ? '63-400' : faker.location.zipCode(),
});

export const getOfferForm = (
  data: FormData,
  recipientType: BUSINESS_TYPE | null,
  withCoowners: number = 0
) => {
  switch (recipientType) {
    case BUSINESS_TYPE.ZOO:
      return {
        ...data.payload?.data,
        owner: {
          address_company: faker.location.street(),
          building_number_company: faker.location.buildingNumber(),
          city_company: faker.location.city(),
          nip_company: generateNip(),
          krs: `00000${faker.helpers
            .rangeToNumber({ min: 10000, max: 99999 })
            .toString()}`,
          regon: generateRegon(),
          zip_code_company: faker.location.zipCode(),
          company_name: faker.company.name(),
          ...ownerBase(data),
          nip: generateNip(),
        },
        ...(withCoowners && {
          coowners: Array.from({ length: withCoowners }, () => ({
            address: faker.location.street(),
            building_number: faker.location.buildingNumber(),
            city: faker.location.city(),
            email: `it+${faker.number.int({ min: 99, max: 10000 })}@helpfind.pl`,
            first_name: faker.person.firstName(),
            last_name: faker.person.lastName(),
            nip: generateNip(),
            pesel: generatePesel(
              faker.date.anytime(),
              faker.helpers.arrayElement(['male', 'female'])
            ),
            phone: faker.helpers.replaceSymbolWithNumber('509######'),
            zip_code: faker.location.zipCode(),
          })),
        }),
      };
    case BUSINESS_TYPE.JDG:
      return {
        ...data.payload?.data,
        owner: {
          ...ownerBase(data),
          nip: generateNip(),
        },
        ...(withCoowners && {
          coowners: Array.from({ length: withCoowners }, () => ({
            address: faker.location.street(),
            building_number: faker.location.buildingNumber(),
            city: faker.location.city(),
            email: `it+${faker.number.int({ min: 99, max: 10000 })}@helpfind.pl`,
            first_name: faker.person.firstName(),
            last_name: faker.person.lastName(),
            phone: faker.helpers.replaceSymbolWithNumber('509######'),
            zip_code: faker.location.zipCode(),
            pesel: generatePesel(
              faker.date.anytime(),
              faker.helpers.arrayElement(['male', 'female'])
            ),
          })),
        }),
      };
    case null:
      return {
        ...data.payload?.data,
        owner: {
          ...ownerBase(data, { controlled: true }),
          tax_office: faker.helpers.arrayElement([
            '371',
            '249',
            '267',
            '296',
            '410',
            '38',
          ]),
        },
        ...(withCoowners && {
          coowners: Array.from({ length: withCoowners }, () => ({
            address: {
              label: 'Kazimierza Pułaskiego',
              value: 198537,
            },
            building_number: faker.location.buildingNumber(),
            city: {
              label: 'Ostrowski > Ostrów Wielkopolski > Ostrów Wielkopolski',
              value: 32719,
            },
            email: `it+${faker.number.int({ min: 99, max: 10000 })}@helpfind.pl`,
            first_name: faker.person.firstName(),
            last_name: faker.person.lastName(),
            pesel: generatePesel(
              faker.date.anytime(),
              faker.helpers.arrayElement(['male', 'female'])
            ),
            phone: faker.helpers.replaceSymbolWithNumber('509######'),
            zip_code: '63-400',
            tax_office: faker.helpers.arrayElement([
              '371',
              '249',
              '267',
              '296',
              '410',
              '38',
            ]),
          })),
        }),
      };
    default:
      return {
        ...surveyFourCompaniesOther,
        owner: {
          address_company: faker.location.street(),
          building_number_company: faker.location.buildingNumber(),
          city_company: faker.location.city(),
          nip_company: generateNip(),
          krs: `0000${faker.helpers
            .rangeToNumber({ min: 100000, max: 999999 })
            .toString()}`,
          zip_code_company: faker.location.zipCode(),
          company_name: faker.company.name(),
          ...ownerBase(data, { controlled: true }),
          nip: generateNip(),
        },
        ...(withCoowners && {
          coowners: Array.from({ length: withCoowners }, () => ({
            address: faker.location.street(),
            building_number: faker.location.buildingNumber(),
            city: faker.location.city(),
            email: `it+${faker.number.int({ min: 99, max: 10000 })}@helpfind.pl`,
            first_name: faker.person.firstName(),
            last_name: faker.person.lastName(),
            nip: generateNip(),
            pesel: generatePesel(
              faker.date.anytime(),
              faker.helpers.arrayElement(['male', 'female'])
            ),
            phone: faker.helpers.replaceSymbolWithNumber('509######'),
            zip_code: faker.location.zipCode(),
          })),
        }),
      };
  }
};

export const dataComplementaryFullAC = {
  accident_reported_to_insurer_complementary: 'yes_my_insure',
  insurance_company_complementary: '80',
  vehicle_value_before_crash_complementary: 'upper_10k',
  accident_in_poland_complementary: 'yes',
  car_condition_complementary: 'repaired',
  wheel_side_complementary: 'left',
  wheel_side_factory_installed_complementary: 'yes',
  insurer_pay_compensation_complementary: 'yes',
  agreement_or_settlement_complementary: 'no',
  still_vehicle_owner_complementary: 'yes',
  unrepairable_damages_before_accident_complementary: 'no',
  repairs_before_accident_complementary: 'no',
  business_activity_complementary: 'no',
  repair_documents_provided_to_ic_complementary: [
    {
      value: 'any',
      label: 'Nie',
      willResetRest: true,
    },
  ],
  damage_rights_assignments_complementary: [
    {
      value: 'any',
      label: 'Nie',
      willResetRest: true,
    },
  ],
};

export const dataComplementaryFullOC = {
  ...dataComplementaryFullAC,
  polish_plates_complementary: 'yes',
};

export const dataComplementaryPartAC = {
  accident_reported_to_insurer_complementary: 'yes_my_insure',
  insurance_company_complementary: '80',
  accident_in_poland_complementary: 'yes',
  car_condition_complementary: 'repaired',
  wheel_side_complementary: 'left',
  wheel_side_factory_installed_complementary: 'yes',
  insurer_pay_compensation_complementary: 'yes',
  agreement_or_settlement_complementary: 'no',
  still_vehicle_owner_complementary: 'yes',
  unrepairable_damages_before_accident_complementary: 'no',
  repairs_before_accident_complementary: 'no',
  business_activity_complementary: 'no',
  repair_documents_provided_to_ic_complementary: [
    {
      value: 'any',
      label: 'Nie',
      willResetRest: true,
    },
  ],
  damage_rights_assignments_complementary: [
    {
      value: 'any',
      label: 'Nie',
      willResetRest: true,
    },
  ],
};

export const dataComplementaryPartOC = {
  ...dataComplementaryPartAC,
  polish_plates_complementary: 'yes',
};
